import { useState, type MouseEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, AlertTriangle, Check, Loader2, RefreshCw } from "lucide-react";
import { getRetailerTrends, type RetailerTrend, type RetailerTrendDay } from "../../services/RetailerTrendsService";
import {
  METRICS,
  baselineAt,
  dayLabel,
  deltaLabel,
  dropOn,
  full,
  isDrop,
  latestAvailable,
  linePath,
  lowCoverageOn,
  niceScale,
  type TrendMetric,
  type TrendMode,
} from "./RetailerTrendsTab.utils";

// Admin → Monitor → Retailer health. Every retailer on one scrolling page:
// users with stores, stores crawled and products uploaded per day, from the
// daily store-analytics snapshots. Hovering any chart shows that day on all of
// them, so a fleet-wide outage lines up across retailers.

const CARD = "bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-700";
const RANGES = [7, 30, 90];
const BASELINE_DAYS = 7; // fetched ahead of the range so its first days have a normal to compare to
const W = 1000;
const H = 200;

function Segmented<T extends string | number>({ label, options, value, onChange }: { label: string; options: { value: T; label: string }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div role="group" aria-label={label} className="flex gap-0.5 rounded-lg bg-gray-100 dark:bg-dark-800 p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
          className={`rounded-md px-3.5 py-1.5 text-[13px] font-semibold transition-colors ${
            o.value === value ? "bg-white dark:bg-dark-600 text-gray-900 dark:text-white shadow-sm" : "text-gray-500 dark:text-dark-300 hover:text-gray-900 dark:hover:text-white"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function TrendChart({
  metric,
  days,
  offset,
  hover,
  onHover,
}: {
  metric: TrendMetric;
  days: RetailerTrendDay[];
  offset: number;
  hover: number | null;
  onHover: (i: number | null) => void;
}) {
  const all = days.map(metric.value);
  const shown = all.slice(offset);
  const second = metric.second ? days.slice(offset).map(metric.second) : null;
  const n = shown.length;
  const pool = [...shown, ...(second ?? [])].filter((v): v is number => v != null);
  const latest = latestAvailable(days, offset) - offset;
  const idx = hover ?? latest;
  const day = idx >= 0 ? days[offset + idx] : undefined;
  const value = idx >= 0 ? shown[idx] : null;
  const base = idx >= 0 ? baselineAt(all, offset + idx) : null;
  const drop = isDrop(value, base);

  if (pool.length === 0) {
    return (
      <div className="flex flex-col gap-2 min-w-0">
        <h4 className="text-sm font-semibold text-gray-600 dark:text-dark-200">{metric.title}</h4>
        <div className="flex h-[140px] items-center justify-center rounded-lg bg-gray-50 dark:bg-dark-900 text-xs text-gray-500 dark:text-dark-300">No snapshots in this range</div>
      </div>
    );
  }

  const scale = niceScale(Math.min(...pool), Math.max(...pool), metric.cap);
  const pct = (i: number) => (n === 1 ? 0 : (i / (n - 1)) * 100);
  const yPct = (v: number) => (1 - (v - scale.lo) / (scale.hi - scale.lo)) * 100;
  const line = linePath(shown, scale.lo, scale.hi, W, H);
  const bandWidth = 100 / Math.max(1, n - 1);
  const xLabels = n <= 1 ? [0] : [0, Math.round((n - 1) / 2), n - 1];

  const onMove = (e: MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const i = Math.max(0, Math.min(n - 1, Math.round(((e.clientX - rect.left) / rect.width) * (n - 1))));
    if (i !== hover) onHover(i);
  };

  return (
    <div className="flex flex-col gap-2.5 min-w-0">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-1 min-w-0">
          <h4 className="text-sm font-semibold text-gray-600 dark:text-dark-200">{metric.title}</h4>
          {metric.legend && (
            <div className="flex gap-3 text-[11px] text-gray-500 dark:text-dark-300">
              <span className="flex items-center gap-1.5"><span className="h-0.5 w-3.5 rounded bg-blue-600 dark:bg-blue-400" />Crawled</span>
              <span className="flex items-center gap-1.5"><span className="w-3.5 border-t-2 border-dashed border-gray-400 dark:border-dark-400" />With users</span>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-0.5 text-right">
          <span className="text-xl font-bold tracking-tight tabular-nums text-gray-900 dark:text-white">{day?.available ? metric.big(day) : "—"}</span>
          <span className="text-[11px] text-gray-500 dark:text-dark-300">
            {day ? `${dayLabel(day.date)}${hover == null ? " · latest" : ""}` : ""}
          </span>
          <span className={`text-xs tabular-nums ${drop ? "font-semibold text-red-700 dark:text-red-400" : "text-gray-500 dark:text-dark-300"}`}>
            {day?.available ? deltaLabel(value, base) : "No snapshot"}
          </span>
        </div>
      </div>
      <div className="pl-11">
        <div className="relative h-[140px] cursor-crosshair" onMouseMove={onMove} onMouseLeave={() => onHover(null)}>
          {shown.map((v, i) =>
            isDrop(v, baselineAt(all, offset + i)) ? (
              <div
                key={`band-${i}`}
                className="absolute inset-y-0 rounded-sm bg-red-500/10 dark:bg-red-400/15"
                style={{ left: `${Math.max(0, pct(i) - bandWidth / 2)}%`, width: `${bandWidth}%` }}
              />
            ) : null,
          )}
          {scale.ticks.map((t) => (
            <div key={t} className="absolute inset-x-0 border-t border-gray-100 dark:border-dark-700" style={{ top: `${yPct(t)}%` }}>
              <span className="absolute right-full -translate-y-1/2 pr-2 text-[11px] tabular-nums text-gray-500 dark:text-dark-300 whitespace-nowrap">{metric.axis(t)}</span>
            </div>
          ))}
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={`${metric.title}, last ${n} days`} className="absolute inset-0 h-full w-full overflow-visible">
            {second && (
              <path d={linePath(second, scale.lo, scale.hi, W, H)} fill="none" strokeWidth={2} strokeDasharray="5 4" vectorEffect="non-scaling-stroke" className="stroke-gray-400 dark:stroke-dark-400" />
            )}
            <path d={line} fill="none" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" className="stroke-blue-600 dark:stroke-blue-400" />
          </svg>
          {hover != null && day && (
            <>
              <div className="pointer-events-none absolute inset-y-0 w-px bg-gray-900/30 dark:bg-white/30" style={{ left: `${pct(idx)}%` }} />
              <div
                className={`pointer-events-none absolute top-1.5 z-10 whitespace-nowrap rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-2.5 py-1.5 shadow-md ${
                  pct(idx) > 50 ? "-translate-x-full -ml-2.5" : "ml-2.5"
                }`}
                style={{ left: `${pct(idx)}%` }}
              >
                <div className="text-[11px] text-gray-500 dark:text-dark-300">{dayLabel(day.date)}</div>
                <div className="text-[13px] font-semibold tabular-nums text-gray-900 dark:text-white">{day.available ? metric.tip(day) : "No snapshot"}</div>
              </div>
            </>
          )}
          {value != null && (
            <div
              className="pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-600 dark:bg-blue-400 ring-2 ring-white dark:ring-dark-800"
              style={{ left: `${pct(idx)}%`, top: `${yPct(value)}%` }}
            />
          )}
        </div>
        <div className="relative mt-1.5 h-5">
          {xLabels.map((i, k) => (
            <span
              key={i}
              className={`absolute whitespace-nowrap text-[11px] text-gray-500 dark:text-dark-300 ${k === 0 ? "" : k === xLabels.length - 1 ? "-translate-x-full" : "-translate-x-1/2"}`}
              style={{ left: `${pct(i)}%` }}
            >
              {dayLabel(days[offset + i].date)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function RetailerCard({ trend, mode, range, hover, onHover }: { trend: RetailerTrend; mode: TrendMode; range: number; hover: number | null; onHover: (i: number | null) => void }) {
  const offset = Math.max(0, trend.days.length - range);
  const latest = latestAvailable(trend.days, offset);
  const last = latest >= 0 ? trend.days[latest] : undefined;
  const drop = dropOn(trend.days, latest);
  const lowCoverage = lowCoverageOn(trend.days, latest);
  const today = trend.days[trend.days.length - 1];

  return (
    <section id={`retailer-health-${trend.retailer}`} className={`${CARD} scroll-mt-20 px-5 py-4 flex flex-col gap-4`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-3">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">{trend.name}</h3>
          {last && (
            <span className="text-[13px] text-gray-500 dark:text-dark-300">
              {full(last.storesWithUsers)} stores with users · {full(last.users)} users
            </span>
          )}
          {today && !today.available && <span className="text-[13px] text-amber-700 dark:text-amber-400">No snapshot for {dayLabel(today.date)} yet</span>}
        </div>
        {drop ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 dark:bg-red-950/40 px-2.5 py-1 text-xs font-semibold text-red-700 dark:text-red-300">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            Drop in {drop.toLowerCase()}
          </span>
        ) : lowCoverage != null ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 dark:bg-amber-950/40 px-2.5 py-1 text-xs font-semibold text-amber-800 dark:text-amber-300">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            Low coverage · {Math.round(lowCoverage)}% of stores crawled
          </span>
        ) : (
          last && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 dark:bg-emerald-950/40 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
              Normal
            </span>
          )
        )}
      </div>
      <div className="grid gap-7 [grid-template-columns:repeat(auto-fit,minmax(280px,1fr))]">
        {METRICS[mode].map((metric) => (
          <TrendChart key={metric.title} metric={metric} days={trend.days} offset={offset} hover={hover} onHover={onHover} />
        ))}
      </div>
    </section>
  );
}

export default function RetailerTrendsTab() {
  const [range, setRange] = useState(30);
  const [mode, setMode] = useState<TrendMode>("total");
  const [hover, setHover] = useState<number | null>(null);
  const fetchDays = Math.min(90, range + BASELINE_DAYS);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["admin-retailer-trends", fetchDays],
    queryFn: () => getRetailerTrends(fetchDays),
    staleTime: 10 * 60_000,
  });

  const retailers = data?.retailers ?? [];
  const jump = (id: string) => document.getElementById(`retailer-health-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Retailer health</h2>
          <p className="text-sm text-gray-500 dark:text-dark-300">
            How the crawlers did each day, from the daily analytics run (~10am ET). Red bands mark days 20%+ below the 7-day normal.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            label="Date range"
            options={RANGES.map((d) => ({ value: d, label: `${d}d` }))}
            value={range}
            onChange={(d) => {
              setRange(d);
              setHover(null);
            }}
          />
          <Segmented
            label="Show values as"
            options={[
              { value: "total" as TrendMode, label: "Total" },
              { value: "per" as TrendMode, label: "Avg per store" },
            ]}
            value={mode}
            onChange={setMode}
          />
          <button
            type="button"
            onClick={() => refetch()}
            aria-label="Refresh"
            className="rounded-lg p-2 text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-dark-800"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {retailers.length > 0 && (
        <nav aria-label="Jump to retailer" className="sticky top-0 z-20 -mx-1 flex flex-wrap gap-1.5 bg-gray-50/95 dark:bg-dark-950/95 px-1 py-2 backdrop-blur">
          {retailers.map((t) => {
            const offset = Math.max(0, t.days.length - range);
            const latest = latestAvailable(t.days, offset);
            const bad = dropOn(t.days, latest) != null;
            const low = !bad && lowCoverageOn(t.days, latest) != null;
            return (
              <button
                key={t.retailer}
                type="button"
                onClick={() => jump(t.retailer)}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800 px-3 py-2 text-sm font-semibold text-gray-800 dark:text-dark-100 hover:bg-gray-50 dark:hover:bg-dark-700"
              >
                {t.name}
                {bad && <span className="h-2 w-2 rounded-full bg-red-600 dark:bg-red-400" aria-label="has a drop" />}
                {low && <span className="h-2 w-2 rounded-full bg-amber-500 dark:bg-amber-400" aria-label="low coverage" />}
              </button>
            );
          })}
        </nav>
      )}

      {isLoading && (
        <div className={`${CARD} flex items-center justify-center gap-2 py-16 text-sm text-gray-500 dark:text-dark-300`}>
          <Loader2 className="h-4 w-4 animate-spin" /> Reading analytics snapshots…
        </div>
      )}
      {error && (
        <div className={`${CARD} flex items-center gap-2 px-5 py-4 text-sm text-red-700 dark:text-red-400`}>
          <AlertCircle className="h-4 w-4" /> {error instanceof Error ? error.message : "Could not load retailer trends"}
        </div>
      )}

      {retailers.map((trend) => (
        <RetailerCard key={trend.retailer} trend={trend} mode={mode} range={range} hover={hover} onHover={setHover} />
      ))}
    </div>
  );
}
